import { useCallback, type RefObject } from "react";
import { toast } from "sonner";
import type { useZero } from "@rocicorp/zero/react";
import { deriveMessageKind } from "@ano/shared/message-kind-derivation";
import { mutators } from "@/lib/mutators";
import { extractMentionIds } from "@/lib/mentions";
import { extractUrls } from "@/lib/url-utils";
import { postSystemLog } from "@/lib/system-log";
import { markSent } from "@/lib/send-trace";
import { fetchAndStoreLinkPreviews } from "@/hooks/use-link-preview";
import { triggerAgentResponse } from "./trigger-agent-response";
import { handleSendError } from "./handle-send-error";
import { useGiphySend } from "./use-giphy-send";
import {
  getAccessTokenWithRetry,
  type AccessTokenGetter,
} from "@/lib/auth-token";
import type { TiptapEditorRef } from "../TiptapEditor";
import type { usePendingAttachments } from "../AttachmentUploader";

interface UseMessageSendArgs {
  channelId: string;
  channelName?: string | null;
  isDM?: boolean;
  threadId?: string;
  /** Inline-reply target. Routes the send via `addInlineReply` when
   *  set and `threadId` is not. */
  replyingToMessageId?: string | null;
  onClearReply?: () => void;
  workspaceId?: string | null;
  isCoworkerDm?: boolean;
  coworkerDmUserId?: string | null;
  coworkerIds?: Set<string>;
  workspaceMembers: { id: string; display_name: string }[];

  user: { id: string; display_name: string } | null | undefined;
  zero: ReturnType<typeof useZero>;
  getAccessToken: AccessTokenGetter;

  editorRef: RefObject<TiptapEditorRef | null>;

  attachments: ReturnType<typeof usePendingAttachments>;

  /**
   * Re-entry guard. Held for the optimistic local apply only (sub-frame),
   * NOT for the server confirm — Zero queues the server replay itself, so
   * the user can keep typing and queue more sends immediately. Lives in a
   * ref (not state) so flipping it doesn't propagate to the editor's
   * `disabled` prop and blur the contenteditable mid-send. See PR #1482's
   * focus-loss postmortem.
   */
  sendingRef: RefObject<boolean>;

  alsoSendToChannel: boolean;
  setAlsoSendToChannel: (next: boolean) => void;

  setGiphyPreview: (next: { searchTerm: string } | null) => void;

  setShowSlashCommands: (open: boolean) => void;
  setShowMentions: (open: boolean) => void;
  clearTyping: () => void;

  onSend?: () => void;
}

interface UseMessageSendResult {
  sendMessage: (options?: { broadcast?: boolean }) => Promise<void>;
  handleGiphySend: (gifUrl: string, searchTerm: string) => Promise<void>;
  handleGiphyCancel: () => void;
}

/**
 * Encapsulates the composer's send pipeline:
 *   • Recognizes /giphy and /run slash-commands and handles them inline.
 *   • Validates content + attachments, uploads attachments, then writes
 *     the message (or thread reply) via Zero mutators.
 *   • Inserts the attachment rows.
 *   • Posts the #logs system message when applicable.
 *   • Triggers coworker SSE / legacy responses for coworker DMs and
 *     channel @mentions of coworkers.
 *   • Restores the composer content if any step fails.
 *
 * Returned `handleGiphySend` stamps `kind='gif'` + `payload={ url,
 * search_term }` with empty content; the gif card in `message-cards.tsx`
 * renders directly from `payload.url`. `handleGiphyCancel` dismisses
 * the preview and refocuses the editor.
 */
export function useMessageSend({
  channelId,
  channelName,
  isDM,
  threadId,
  replyingToMessageId,
  onClearReply,
  workspaceId,
  isCoworkerDm,
  coworkerDmUserId,
  coworkerIds,
  workspaceMembers,
  user,
  zero,
  getAccessToken,
  editorRef,
  attachments,
  sendingRef,
  alsoSendToChannel,
  setAlsoSendToChannel,
  setGiphyPreview,
  setShowSlashCommands,
  setShowMentions,
  clearTyping,
  onSend,
}: UseMessageSendArgs): UseMessageSendResult {
  const { pendingAttachments, uploadAttachments, clearAttachments } =
    attachments;

  const sendMessage = useCallback(
    async (options?: { broadcast?: boolean }) => {
      const rawMarkdown = editorRef.current?.getMarkdown() ?? "";
      const trimmedContent = rawMarkdown.trim();

      if (/^\/giphy(\s|$)/i.test(trimmedContent)) {
        const term = trimmedContent.replace(/^\/giphy\s*/i, "").trim();
        if (!term) {
          toast.error('Please provide a search term, e.g. "/giphy cats"');
          return;
        }
        setGiphyPreview({ searchTerm: term });
        editorRef.current?.clear();
        setShowSlashCommands(false);
        clearTyping();
        return;
      }

      if (/^\/run(\s|$)/i.test(trimmedContent)) {
        const cmd = trimmedContent.replace(/^\/run\s*/i, "").trim();
        if (!cmd) {
          toast.error('Please provide a command, e.g. "/run npm test"');
          return;
        }
        editorRef.current?.clear();
        setShowSlashCommands(false);
        clearTyping();
        window.dispatchEvent(new CustomEvent("terminal:open"));
        window.dispatchEvent(
          new CustomEvent("terminal:run-command", {
            detail: { command: cmd, channelId, userId: user?.id },
          }),
        );
        return;
      }

      const hasContent = trimmedContent.length > 0;
      const hasAttachments = pendingAttachments.length > 0;

      if ((!hasContent && !hasAttachments) || !user || sendingRef.current)
        return;

      sendingRef.current = true;

      try {
        const messageContent = trimmedContent;
        const mentionIds = extractMentionIds(messageContent, workspaceMembers);
        const mentions = mentionIds.length > 0 ? mentionIds : undefined;
        const messageId = crypto.randomUUID();
        const now = Date.now();

        let uploadedAttachments: Awaited<ReturnType<typeof uploadAttachments>> =
          [];
        if (hasAttachments) {
          const token = await getAccessTokenWithRetry(getAccessToken, {
            maxWaitMs: 3000,
            intervalMs: 200,
          });
          if (!token) {
            console.error(
              "[message-input] missing token for attachment upload",
            );
            toast.error("Could not authenticate for file upload");
            sendingRef.current = false;
            return;
          }
          uploadedAttachments = await uploadAttachments(token);
          // Block the send if ANY attachment failed to upload — not only when
          // ALL fail. `uploadAttachments` stops at the first failure and
          // returns just the prefix that succeeded; sending with that
          // truncated set silently drops the remaining files while the
          // message still appears sent (often with a media kind). Compare
          // against the count the user actually attached.
          if (uploadedAttachments.length < pendingAttachments.length) {
            toast.error(
              uploadedAttachments.length === 0
                ? "All file uploads failed. Message not sent."
                : "Some files failed to upload. Message not sent — please retry.",
            );
            sendingRef.current = false;
            return;
          }
        }

        const isBroadcast = !!(
          threadId &&
          (options?.broadcast || alsoSendToChannel)
        );

        // Phase 7b — derive kind + payload from composer intent (post-upload,
        // since file_category / file_type are server-derived). Picker `.gif`
        // uploads stamp `kind='gif'` (Phase 7b PR C); other media kinds —
        // image/video/audio/file/media_group/link_only — fall through to the
        // existing per-attachment dispatch unchanged.
        const finalContent =
          messageContent || (uploadedAttachments.length > 0 ? "" : "");
        const derived = deriveMessageKind({
          content: finalContent,
          attachments: uploadedAttachments,
        });
        const linkPreviewUrls = extractUrls(finalContent).slice(0, 5);

        // Routing: threadId → addReply (thread); replyingToMessageId →
        // addInlineReply (citation); neither → insert (top-level).
        const replyParentId = threadId ?? replyingToMessageId ?? null;
        const isInlineReply = !threadId && !!replyingToMessageId;

        // Phase 7b PR D2 — atomic mutator path. When the send carries
        // attachments, use `*WithAttachments` so message + attachment
        // INSERTs commit/roll-back together (closes the partial-failure
        // window where `kind='image'` could land without attachment
        // rows). The empty-array case has already been short-circuited
        // by the upload-failure guard above.
        const attachmentRows = uploadedAttachments.length
          ? uploadedAttachments.map((a) => ({
              id: a.id,
              message_id: messageId,
              user_id: user.id,
              filename: a.filename,
              file_type: a.file_type,
              file_size: a.file_size,
              file_category: a.file_category,
              storage_key: a.storage_key,
              storage_url: a.storage_url,
              thumbnail_url: a.thumbnail_url,
              width: a.width,
              height: a.height,
              created_at: now,
            }))
          : null;

        if (replyParentId) {
          markSent(
            messageId,
            channelId,
            threadId ? "addReply" : "inline-addReply",
          );
          const replyBody = {
            id: messageId,
            user_id: user.id,
            content: finalContent,
            kind: derived.kind,
            payload: derived.payload,
            is_broadcast: isBroadcast,
            is_edited: false,
            ...(mentions ? { mentions } : {}),
            created_at: now,
            updated_at: now,
          };
          const result = zero.mutate(
            isInlineReply
              ? attachmentRows
                ? mutators.messages.addInlineReplyWithAttachments({
                    parent_id: replyParentId,
                    reply: replyBody,
                    attachments: attachmentRows,
                  })
                : mutators.messages.addInlineReply({
                    parent_id: replyParentId,
                    reply: replyBody,
                  })
              : attachmentRows
                ? mutators.messages.addReplyWithAttachments({
                    parent_id: replyParentId,
                    reply: replyBody,
                    attachments: attachmentRows,
                  })
                : mutators.messages.addReply({
                    parent_id: replyParentId,
                    reply: replyBody,
                  }),
          );
          // Await the optimistic apply BEFORE clearing the editor so a
          // mutator rejection (auth, schema mismatch, FK violation on
          // attachments) gets restored content + a toast instead of
          // silently disappearing into console.error. Throwing here
          // hands the error to the surrounding try/catch, which already
          // handles restore + toast.
          const res = await result.client;
          if (res.type === "error") {
            throw new Error(
              `addReply failed: ${res.error instanceof Error ? res.error.message : String(res.error)}`,
            );
          }
          // Inline replies with attachments now route through the atomic
          // `addInlineReplyWithAttachments` mutator above (message +
          // attachments in one transaction), so there is no separate
          // fire-and-forget attachment insert here anymore.
          // Drop inline-reply target after a successful queue (the
          // optimistic write already happened). User starts fresh next
          // time. Thread sends keep their threadId; only inline replies
          // need this reset.
          //
          // `onSend` semantics: thread sends do NOT fire it (preserving
          // pre-mig-130 behavior — sidebar composer manages its own
          // post-send state). Inline replies DO fire it because they're
          // mounted in the channel composer alongside top-level sends
          // and consumers expect editor-clear etc.
          if (!threadId && replyingToMessageId) {
            onClearReply?.();
            onSend?.();
          }
        } else {
          markSent(messageId, channelId, "insert");
          const messageBody = {
            id: messageId,
            channel_id: channelId,
            user_id: user.id,
            content: finalContent,
            kind: derived.kind,
            payload: derived.payload,
            is_edited: false,
            is_broadcast: isBroadcast,
            ...(mentions ? { mentions } : {}),
            created_at: now,
            updated_at: now,
          };
          const result = zero.mutate(
            attachmentRows
              ? mutators.messages.insertWithAttachments({
                  message: messageBody,
                  attachments: attachmentRows,
                })
              : mutators.messages.insert(messageBody),
          );
          const res = await result.client;
          if (res.type === "error") {
            throw new Error(
              `insert failed: ${res.error instanceof Error ? res.error.message : String(res.error)}`,
            );
          }
          onSend?.();
        }

        if (linkPreviewUrls.length > 0) {
          void (async () => {
            const token = await getAccessTokenWithRetry(
              async () => (await getAccessToken()) ?? null,
              {
                maxWaitMs: 3000,
                intervalMs: 200,
              },
            );
            if (!token) return;
            await fetchAndStoreLinkPreviews(
              zero,
              messageId,
              linkPreviewUrls,
              token,
            );
          })();
        }

        if (channelName && channelName !== "logs" && !isDM) {
          postSystemLog(
            `${user.display_name} sent a message in #${channelName}`,
          );
        }

        // Trigger the agent response — fire-and-forget. Routes
        // coworker DMs / mentions to SSE or the legacy REST fallback
        // depending on workspace + coworker context. See
        // `./trigger-agent-response.ts` for the routing rules.
        triggerAgentResponse({
          channelId,
          workspaceId,
          isCoworkerDm,
          coworkerDmUserId,
          coworkerIds,
          mentionIds,
          messageId,
          messageContent,
          getAccessToken,
        });

        editorRef.current?.clear();
        clearAttachments();
        setShowMentions(false);
        setAlsoSendToChannel(false);
        clearTyping();
      } catch (err) {
        // Restore the user's message into the composer so they can
        // retry without retyping. The captured `trimmedContent`
        // snapshot is used because the editor may have already been
        // cleared by an earlier successful step (e.g. the optimistic
        // mutation landed before a downstream attachment fail).
        // See `./handle-send-error.ts` for the Sentry capture +
        // restoration shape.
        handleSendError(err, trimmedContent, editorRef, {
          channelId,
          isDM: !!isDM,
          hasReplyParent: !!(threadId ?? replyingToMessageId),
          hasAttachments,
          isCoworkerDm: !!isCoworkerDm,
        });
      } finally {
        sendingRef.current = false;
      }
    },
    [
      channelId,
      channelName,
      isDM,
      threadId,
      replyingToMessageId,
      onClearReply,
      workspaceId,
      isCoworkerDm,
      coworkerDmUserId,
      coworkerIds,
      workspaceMembers,
      user,
      zero,
      getAccessToken,
      editorRef,
      pendingAttachments.length,
      uploadAttachments,
      clearAttachments,
      sendingRef,
      alsoSendToChannel,
      setAlsoSendToChannel,
      setGiphyPreview,
      setShowSlashCommands,
      setShowMentions,
      clearTyping,
      onSend,
    ],
  );

  const { handleGiphySend, handleGiphyCancel } = useGiphySend({
    channelId,
    threadId,
    replyingToMessageId,
    onClearReply,
    user,
    zero,
    editorRef,
    setGiphyPreview,
    onSend,
  });

  return { sendMessage, handleGiphySend, handleGiphyCancel };
}
